import { useEffect } from 'react';
import { useFullyReadMarkerStore } from '../store/use-fully-read-marker-store';
import { useRawTimelineStore } from '../store/use-timeline-store';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { isChannelStreamId, spaceIdFromChannelId } from '@towns-protocol/sdk';
import { create } from 'zustand';
const useSpaceMentionsStore = create((set) => ({
    mentionsMap: {},
    setMentions: (spaceId, mentions) => set((prev) => {
        if (isEqual(prev.mentionsMap[spaceId]?.mentions, mentions)) {
            return prev;
        }
        const unreadThreadCount = mentions.reduce((count, m) => {
            return m.thread && m.unread ? count + 1 : count;
        }, 0);
        const unreadChannelCount = mentions.reduce((count, m) => {
            return !m.thread && m.unread ? count + 1 : count;
        }, 0);
        const next = { mentions, unreadThreadCount, unreadChannelCount };
        return { mentionsMap: { ...prev.mentionsMap, [spaceId]: next } };
    }),
}));
export function useCalculateSpaceMentions(_opts) {
    useEffect(() => {
        const runUpdate = () => {
            const unreadMarkers = useFullyReadMarkerStore.getState().markers;
            const threadsStats = useRawTimelineStore.getState().threadsStats;
            const timelines = useRawTimelineStore.getState().timelines;
            const mentionsMap = {};
            for (const [streamId, timeline] of Object.entries(timelines)) {
                if (!isChannelStreamId(streamId)) {
                    continue;
                }
                const channelId = streamId;
                const spaceId = spaceIdFromChannelId(channelId);
                if (!timeline?.length) {
                    return;
                }
                let mentions = mentionsMap[spaceId];
                if (!mentions) {
                    mentions = [];
                    mentionsMap[spaceId] = mentions;
                }
                const channelMentions = timeline
                    .filter((event) => event.isMentioned)
                    .map((event) => {
                    const threadStat = event.threadParentId
                        ? threadsStats[channelId]?.[event.threadParentId]
                        : undefined;
                    const fullyReadMarker = unreadMarkers[event.threadParentId ?? channelId];
                    return {
                        type: 'mention',
                        unread: fullyReadMarker?.isUnread === true &&
                            event.eventNum >= fullyReadMarker.eventNum,
                        channelId,
                        timestamp: event.createdAtEpochMs,
                        event,
                        thread: threadStat?.parentEvent,
                    };
                });
                mentions.push(...channelMentions);
            }
            for (const [spaceId, mentions] of Object.entries(mentionsMap)) {
                mentions.sort(
                //firstBy<MentionResult>((m) => (m.unread ? 0 : 1)).thenBy((a) => a.timestamp, -1),
                (a, b) => {
                    if (a.unread && !b.unread) {
                        return -1;
                    }
                    else if (!a.unread && b.unread) {
                        return 1;
                    }
                    else if (a.timestamp > b.timestamp) {
                        return -1;
                    }
                    else if (a.timestamp < b.timestamp) {
                        return 1;
                    }
                    else {
                        return 0;
                    }
                });
                useSpaceMentionsStore.getState().setMentions(spaceId, mentions);
            }
        };
        const debouncedRunUpdate = debounce(runUpdate, 2000, { maxWait: 2000 });
        const onTimelineStoreChange = (state, prevState) => {
            if (state.threadsStats !== prevState.threadsStats ||
                state.timelines !== prevState.timelines) {
                debouncedRunUpdate();
            }
        };
        runUpdate();
        const unsub1 = useFullyReadMarkerStore.subscribe(debouncedRunUpdate);
        const unsub2 = useRawTimelineStore.subscribe(onTimelineStoreChange);
        return () => {
            unsub1();
            unsub2();
        };
    }, []);
}
const emptyMentionsResult = [];
export function useSpaceMentions(spaceId) {
    return useSpaceMentionsStore((state) => spaceId ? state.mentionsMap[spaceId]?.mentions ?? emptyMentionsResult : emptyMentionsResult);
}
export function useSpaceUnreadThreadMentions(spaceId) {
    return useSpaceMentionsStore((state) => spaceId ? state.mentionsMap[spaceId]?.unreadThreadCount ?? 0 : 0);
}
export function useSpaceUnreadChannelMentions(spaceId) {
    return useSpaceMentionsStore((state) => spaceId ? state.mentionsMap[spaceId]?.unreadChannelCount ?? 0 : 0);
}
//# sourceMappingURL=use-space-mentions.js.map